import { createEventId } from "./normalize.js";
import type {
  CaptureOptions,
  ErrorPayload,
  TelemetryContext,
  TelemetryEvent,
  TelemetryEventKind,
  TelemetryFramework,
  TelemetryLevel,
  TelemetryMessageLevel,
  TelemetryRuntime,
} from "./types.js";

export interface BuildEventBase {
  runtime: TelemetryRuntime;
  framework: TelemetryFramework;
  context: TelemetryContext;
}

function baseFields(
  kind: TelemetryEventKind,
  base: BuildEventBase,
): Pick<
  TelemetryEvent,
  "id" | "timestamp" | "runtime" | "kind" | "framework" | "context"
> {
  return {
    id: createEventId(),
    timestamp: new Date().toISOString(),
    runtime: base.runtime,
    kind,
    framework: base.framework,
    context: base.context,
  };
}

export function messageLevelToTelemetryLevel(
  level: TelemetryMessageLevel,
): TelemetryLevel {
  if (level === "warning") return "warn";
  return level;
}

export function buildErrorEvent(
  kind: "error" | "unhandled_rejection" | "route_error",
  error: ErrorPayload,
  base: BuildEventBase,
  options: CaptureOptions = {},
): TelemetryEvent {
  return {
    ...baseFields(kind, base),
    error,
    componentStack: options.componentStack,
    routeId: options.routeId,
    handled: options.handled,
    metadata: options.metadata,
  };
}

export function buildResourceErrorEvent(
  message: string,
  base: BuildEventBase,
  metadata?: Record<string, unknown>,
): TelemetryEvent {
  return {
    ...baseFields("resource_error", base),
    error: { name: "ResourceError", message },
    metadata,
    handled: false,
  };
}

export function buildLogEvent(
  level: TelemetryLevel,
  message: string,
  base: BuildEventBase,
  attributes?: Record<string, unknown>,
): TelemetryEvent {
  return {
    ...baseFields("log", base),
    message,
    level,
    attributes,
  };
}

export function buildMessageEvent(
  message: string,
  level: TelemetryMessageLevel,
  base: BuildEventBase,
  attributes?: Record<string, unknown>,
): TelemetryEvent {
  return {
    ...baseFields("message", base),
    message,
    level: messageLevelToTelemetryLevel(level),
    attributes,
  };
}

export function buildCustomEvent(
  name: string,
  message: string,
  base: BuildEventBase,
  metadata?: Record<string, unknown>,
): TelemetryEvent {
  return {
    ...baseFields("custom", base),
    message,
    level: "info",
    attributes: { name, ...metadata },
    metadata,
  };
}
